import { action, observable } from 'mobx';

import { ApiStore } from './ApiStore';
import { provideSingleton, inject } from '../utils/decorators';
import { SesAufstellflaechenTyp } from '../models/ApiTypesSes';
import { ISesMesswertDatensatz, ISesBeurteilungsErgebnis, ISesMessung } from '../models/ApiTypesSes';

@provideSingleton(SesAuswertungFlaechenRili2025Store)
export class SesAuswertungFlaechenRili2025Store {

  @inject(ApiStore) apiStore: ApiStore;

  messRaster = 250;

  @observable messwerteFahrzeug: ISesMessung = {
    messpunkte: [],
    sperrpunkte: [],
    sperrflaechen: [],
    messwerte: {
      links: [],
      rechts: []
    },
    fahrspur: {
      breite: '',
      laenge: ''
    },
    flaeche: {
      steigung: '',
      achsenabschnitt: '0'
    },
    typ: undefined
  };

  @observable messwerteSep: ISesMessung = {
    messpunkte: ['0', '250', '500', '750', '1000', '1250', '1500', '1750', '2000', '2250', '2500'],
    sperrpunkte: [],
    sperrflaechen: [],
    messwerte: {
      links: ['0', '', '', '', '', '', '', '', '', '', ''],
      rechts: ['', '', '', '', '', '', '', '', '', '', '']
    },
    fahrspur: {
      breite: '',
      laenge: '2500'
    },
    flaeche: {
      steigung: '',
      achsenabschnitt: '0'
    },
    typ: undefined,
    richtlinie: '2025'
  };

  @observable beurteilungFahrzeug: ISesBeurteilungsErgebnis = {
    beurteilung: [],
    neigungen: {
      quer: 0,
      laengs: 0,
      ermittelt: 0
    }
  };

  @observable beurteilungSep: ISesBeurteilungsErgebnis = {
    beurteilung: [],
    neigungen: {
      quer: 0,
      laengs: 0,
      ermittelt: 0
    }
  };

  @observable zeitpunktGespeichert: string | undefined = undefined;
  @observable gespeicherteEingabenVorhanden: boolean = false;

  onSperrFlaecheGeaendert: () => void;

  editable: boolean = true;

  private validateTimeoutRef: number = 0;
  private validateTimeOutTyp: SesAufstellflaechenTyp | undefined = undefined;

  validateMesswerteDelayed(aufstellflaechenTyp: SesAufstellflaechenTyp, maximaleSpurAnzahl: number) {
    if ((this.validateTimeoutRef > 0) && (this.validateTimeOutTyp === aufstellflaechenTyp)) {
      window.clearTimeout(this.validateTimeoutRef);
      this.validateTimeoutRef = 0;
    }

    const validateTimeoutInMs = 400;
    this.validateTimeOutTyp = aufstellflaechenTyp;
    this.validateTimeoutRef = window.setTimeout(
      () => {
        this.validateMesswerte(aufstellflaechenTyp, maximaleSpurAnzahl);
      },
      validateTimeoutInMs
    );
  }

  // @todo: Für Sperrflächen anpassen
  async validateMesswerte(aufstellflaechenTyp: SesAufstellflaechenTyp, maximaleSpurAnzahl: number) {
    if (aufstellflaechenTyp === 'fahrzeug') {
      let messung: ISesMessung = {
        messpunkte: this.messwerteFahrzeug.messpunkte,
        messwerte: {
          links: this.messwerteFahrzeug.messwerte.links,
          rechts: this.messwerteFahrzeug.messwerte.rechts
        },
        fahrspur: this.messwerteFahrzeug.fahrspur,
        flaeche: this.messwerteFahrzeug.flaeche,
        typ: this.messwerteFahrzeug.typ,
        sperrpunkte: [],
        sperrflaechen: this.messwerteFahrzeug.sperrflaechen
      };

      messung.messpunkte = messung.messpunkte.slice(0, maximaleSpurAnzahl);
      messung.messwerte.links = messung.messwerte.links.slice(0, maximaleSpurAnzahl);
      messung.messwerte.rechts = messung.messwerte.rechts.slice(0, maximaleSpurAnzahl);

      const response = await this.apiStore.beurteilungFahrzeugAufstellflaecheRili2025(messung);
      if (response.ok) {
        this.setBeurteilungFahrzeug(response.json);
      }
    } else if (aufstellflaechenTyp === 'sep') {
      const response = await this.apiStore.beurteilungSepAufstellflaeche(this.messwerteSep);
      if (response.ok) {
        this.setBeurteilungSep(response.json);
      }
    }
  }

  @action clear() {
    this.messwerteFahrzeug.messpunkte = [];
    this.messwerteFahrzeug.messwerte.links = [];
    this.messwerteFahrzeug.messwerte.rechts = [];
    this.messwerteFahrzeug.sperrpunkte = [];
    for (let i = 0; i < (8000 / this.messRaster) + 1; i++) {
      this.messwerteFahrzeug.messpunkte.push((i * this.messRaster).toString());
      this.messwerteFahrzeug.messwerte.links.push(i === 0 ? '0' : '');
      this.messwerteFahrzeug.messwerte.rechts.push('');
    }

    this.messwerteFahrzeug.fahrspur.breite = '';
    this.messwerteFahrzeug.fahrspur.laenge = '';
    this.messwerteFahrzeug.flaeche.steigung = '';
    this.messwerteFahrzeug.flaeche.achsenabschnitt = '0';
    this.messwerteFahrzeug.typ = undefined;
    this.messwerteFahrzeug.sperrflaechen = [];

    this.messwerteSep.messpunkte = ['0', '250', '500', '750', '1000', '1250', '1500', '1750', '2000', '2250', '2500'];
    this.messwerteSep.messwerte.links = ['0', '', '', '', '', '', '', '', '', '', ''];
    this.messwerteSep.messwerte.rechts = ['', '', '', '', '', '', '', '', '', '', ''];
    this.messwerteSep.fahrspur.breite =  '';
    this.messwerteSep.fahrspur.laenge = '2500';
    this.messwerteSep.flaeche.steigung = '';
    this.messwerteSep.flaeche.achsenabschnitt = '0';
    this.messwerteSep.typ = undefined;
    this.messwerteSep.richtlinie = '2025';
    this.messwerteSep.sperrflaechen = [];

    this.beurteilungFahrzeug.beurteilung = [];
    this.beurteilungSep.beurteilung = [];
  }

  @action setBeurteilungFahrzeug(ergebnis: ISesBeurteilungsErgebnis) {
    this.beurteilungFahrzeug = ergebnis;
  }

  @action setBeurteilungSep(ergebnis: ISesBeurteilungsErgebnis) {
    this.beurteilungSep = ergebnis;
  }

  @action private setZeitpunktGespeichert(value: string | undefined) {
    this.zeitpunktGespeichert = value;
  }

  @action setEditable(value: boolean) {
    this.editable = value;
  }

  public inSperrFlaeche(abstand: number): boolean {
    var ergebnis = false;
    for (let sperrflaeche of this.messwerteFahrzeug.sperrflaechen) {
      if (sperrflaeche.art === 'sperrflaeche' && abstand >= sperrflaeche.start && abstand <= sperrflaeche.ende) {
        ergebnis = true;
        break;
      }
    }
    return ergebnis;
  }

  uebernehmeMessung(quelle: ISesMessung, ziel: ISesMessung) {
    // Im JSON werden leere Felder als null gespeichert, Inputs erwarten dann aber einen Leerstring, also übernehmen wir nur wirklich zwischengespeicherte Werte
    for (let i = 0; i < quelle.messwerte.links.length; i++) {
      if (quelle.messwerte.links[i] !== null) { ziel.messwerte.links[i] = quelle.messwerte.links[i]; }
    }
    for (let i = 0; i < quelle.messwerte.rechts.length; i++) {
      if (quelle.messwerte.rechts[i] !== null) { ziel.messwerte.rechts[i] = quelle.messwerte.rechts[i]; }
    }
    for (let i = 0; i < quelle.sperrflaechen.length; i++) {
      if (quelle.sperrflaechen[i] !== null) { ziel.sperrflaechen[i] = quelle.sperrflaechen[i]; }
    }
    if (quelle.fahrspur.breite !== null) { ziel.fahrspur.breite = quelle.fahrspur.breite; }
    if (quelle.fahrspur.laenge !== null) { ziel.fahrspur.laenge = quelle.fahrspur.laenge; }
    if (quelle.flaeche.steigung !== null) { ziel.flaeche.steigung = quelle.flaeche.steigung; }
    if (quelle.flaeche.achsenabschnitt !== null) { ziel.flaeche.achsenabschnitt = quelle.flaeche.achsenabschnitt; }
  }

  @action setMesswerteFahrzeug(messwerte: ISesMessung) {
    this.uebernehmeMessung(messwerte, this.messwerteFahrzeug);
  }

  @action setMesswerteSep(messwerte: ISesMessung) {
    this.uebernehmeMessung(messwerte, this.messwerteSep);
  }

  @action setGespeicherteEingabenVorhanden(wert: boolean) {
    this.gespeicherteEingabenVorhanden = wert;
  }

  async eingabenSpeichern(vorgangId: string) {
    const response = await this.apiStore.eingabenAuswertungSesSpeichern(
      vorgangId,
      this.messwerteFahrzeug,
      this.messwerteSep
    );
    if (response.ok) {
      const zeitpunkt = new Date();
      this.setZeitpunktGespeichert(zeitpunkt.toLocaleDateString() + ' ' + zeitpunkt.toLocaleTimeString());
    }
  }

  async eingabenLaden(vorgangId: string) {
    const response = await this.apiStore.eingabenAuswertungSesLaden(vorgangId);
    if (response.ok) {
      if (response.json.wert) {
        try {
          const mw: ISesMesswertDatensatz = JSON.parse(response.json.wert);
          this.setMesswerteFahrzeug(mw.messdaten.fahrzeug);
          this.setMesswerteSep(mw.messdaten.sep);
          this.setGespeicherteEingabenVorhanden(true);
        } catch (e) {
          console.log('Eingabedaten konnten nicht geladen werden');
        }
      }
    }
  }


}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/stores/SesAuswertungFlaechenRili2025Store.ts